import { ClassroomAndStudentSelector } from "@features/assignments/SingleAssignment/componnets/ClassroomAndStudentSelector";
import { Button, Text } from "@core/index";
import { Check, X } from "lucide-react";
import { assignInteractiveMaterial } from "@packages/api/madlenAPI";
import { DataTag, QueryKey, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { StudentInteractiveMaterialResponseDto } from "@packages/api/madlenAPI.schemas";
import { useState } from "react";
import { Input } from "@core/input";

// Date formatting helper functions
const formatDateForInput = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toISOString().slice(0, 16); // "yyyy-MM-ddThh:mm" format
};

const createISOFromInput = (inputValue: string): string => {
  const localDate = new Date(inputValue);
  const timezoneOffset = localDate.getTimezoneOffset();
  const utcDate = new Date(localDate.getTime() - timezoneOffset * 60000);
  return utcDate.toISOString();
};

const AssignTab = ({
  selectedStudents,
  setSelectedStudents,
  setShowAssignTab,
  id,
  assignedStudentsQueryKey,
  refetchAssignedStudents,
  assignedStudentIds,
}: {
  selectedStudents: {
    classroomId: number;
    studentIds: number[];
  }[];
  setSelectedStudents: (
    students: {
      classroomId: number;
      studentIds: number[];
    }[],
  ) => void;
  setShowAssignTab: (show: boolean) => void;
  id: string;
  assignedStudentsQueryKey: DataTag<
    QueryKey,
    StudentInteractiveMaterialResponseDto[]
  >;
  refetchAssignedStudents: () => void;
  assignedStudentIds: number[];
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const currentDateISO = new Date().toISOString();
  const twoWeeksLater = new Date(currentDateISO);
  twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);

  const [endDate, setEndDate] = useState<string>(twoWeeksLater.toISOString());

  return (
    <div className="mt-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-2">
        <Text variant={"h2"} className="mb-2 sm:mb-0">
          assignments.generator.steps.assignButton
        </Text>
        <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
          <Button
            onClick={() => setShowAssignTab(false)}
            size="md"
            rounded="lg"
            bgColor="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700"
            textColor="text-gray-600 dark:text-gray-400"
            className="flex items-center justify-center gap-2"
          >
            <X className="w-5 h-5" />
            {t("common.cancel")}
          </Button>
          <Button
            onClick={async () => {
              await assignInteractiveMaterial(Number(id), {
                endDate: endDate,
                startDate: new Date().toISOString(),
                assignedStudentsWithClassrooms: selectedStudents,
              });
              refetchAssignedStudents();
              queryClient.invalidateQueries({
                queryKey: assignedStudentsQueryKey,
              });
              setShowAssignTab(false);
              setSelectedStudents([]);
            }}
            disabled={selectedStudents.length === 0}
            size="md"
            rounded="lg"
            bgColor="bg-rose-100 dark:bg-rose-900/30 hover:bg-rose-200 dark:hover:bg-rose-900/50"
            textColor="text-rose-600 dark:text-rose-400"
            className={`flex items-center justify-center gap-2 ${
              selectedStudents.length === 0
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            <Check className="w-5 h-5" />
            {t("interactive_material.assignStudents")}
          </Button>
        </div>
      </div>

      <div className="mb-6 mt-4">
        <label
          htmlFor="endDate"
          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
        >
          {t("podcast.endDate")}
        </label>
        <Input
          type="datetime-local"
          value={formatDateForInput(endDate)}
          min={formatDateForInput(currentDateISO)}
          onChange={(e) => {
            setEndDate(createISOFromInput(e.target.value));
          }}
          classNames={{
            input:
              "w-full sm:w-64 p-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600",
          }}
          placeholder={t("podcast.selectEndDate")}
        />
      </div>

      <ClassroomAndStudentSelector
        selectedStudents={selectedStudents}
        setSelectedStudents={setSelectedStudents}
        assignedStudentIds={assignedStudentIds}
      />
    </div>
  );
};

export default AssignTab;
